<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    @click="handleClick"
  >
    <!-- Loading spinner -->
    <span v-if="loading" class="mr-2">
      <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </span>

    <!-- Icon (left) -->
    <span v-if="iconLeft && !loading" class="mr-2">
      <slot name="icon-left">{{ iconLeft }}</slot>
    </span>

    <!-- Default slot (button text) -->
    <slot></slot>

    <!-- Icon (right) -->
    <span v-if="iconRight" class="ml-2">
      <slot name="icon-right">{{ iconRight }}</slot>
    </span>
  </button>
</template>

<script>
export default {
  name: 'BaseButton',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => [
        'primary',
        'secondary',
        'danger',
        'success',
        'warning',
        'info',
        'outline-primary',
        'outline-secondary',
        'outline-danger',
        'ghost',
        'icon'
      ].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value)
    },
    type: {
      type: String,
      default: 'button',
      validator: (value) => ['button', 'submit', 'reset'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    iconLeft: {
      type: String,
      default: null
    },
    iconRight: {
      type: String,
      default: null
    },
    rounded: {
      type: String,
      default: 'md',
      validator: (value) => ['none', 'sm', 'md', 'lg', 'xl', 'full'].includes(value)
    }
  },
  computed: {
    buttonClasses() {
      return [
        // Base classes
        'inline-flex items-center justify-center font-medium text-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2',

        // Size classes
        this.sizeClasses,

        // Variant classes
        this.variantClasses,

        // Rounded classes
        this.roundedClasses,

        // Block class
        this.block ? 'w-full' : '',

        // Disabled state
        (this.disabled || this.loading) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
      ]
    },
    sizeClasses() {
      const sizes = {
        xs: 'px-2 py-1 text-xs',
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-base',
        lg: 'px-6 py-3 text-lg',
        xl: 'px-8 py-4 text-xl'
      }

      // Icon button has equal padding
      if (this.variant === 'icon') {
        const iconSizes = {
          xs: 'p-1',
          sm: 'p-2',
          md: 'p-2.5',
          lg: 'p-3',
          xl: 'p-4'
        }
        return iconSizes[this.size]
      }

      return sizes[this.size]
    },
    variantClasses() {
      const variants = {
        primary: 'bg-primary text-white border border-primary hover:bg-primary-dark hover:border-primary-dark focus:ring-primary-light',

        secondary: 'bg-secondary text-white border border-secondary hover:bg-secondary-dark hover:border-secondary-dark focus:ring-secondary-light',

        danger: 'bg-danger text-white border border-danger hover:bg-red-700 focus:ring-red-500',

        success: 'bg-success text-white border border-success hover:bg-green-600 focus:ring-green-500',

        warning: 'bg-warning text-gray-900 border border-warning hover:bg-yellow-500 focus:ring-yellow-500',

        info: 'bg-info text-white border border-info hover:bg-cyan-700 focus:ring-cyan-500',

        'outline-primary': 'bg-transparent text-primary border border-primary hover:bg-primary hover:text-white focus:ring-primary-light',

        'outline-secondary': 'bg-transparent text-secondary border border-secondary hover:bg-secondary hover:text-white focus:ring-secondary-light',

        'outline-danger': 'bg-transparent text-danger border border-danger hover:bg-danger hover:text-white focus:ring-red-500',

        ghost: 'bg-transparent text-gray-700 border border-transparent hover:bg-gray-100 focus:ring-gray-300',

        icon: 'bg-transparent text-gray-700 border border-transparent hover:bg-gray-100 focus:ring-gray-300 rounded-full'
      }

      return variants[this.variant]
    },
    roundedClasses() {
      if (this.variant === 'icon') return 'rounded-full'

      const rounded = {
        none: 'rounded-none',
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        full: 'rounded-full'
      }

      return rounded[this.rounded]
    }
  },
  methods: {
    handleClick(event) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', event)
      }
    }
  }
}
</script>
